import _Dynamsoft from 'dwt/dist/dynamsoft.webtwain.min.js'
import { useEffect, useState } from 'react'
import formatDate from 'date-fns/format'
import { v4 as uuid } from 'uuid'
import { captureError } from 'Logic/ErrorBoundary.js'
import './twain.ui.css'

let width = '100%'
let height = '400'
let ProductKey = process.env.REACT_APP_DYNAMSOFT_API_KEY
// Importing Dynamsoft like this because of how Vite bundles it
// and for compatibility with CRA. The default import 'dwt' tries to load
// and ESM module that fails because process.version isn't set. When trying
// to set it, eg by using define: { 'process.version': '"17.0.0"'} in vite.config.js
// it fails in another line because import.meta.url isn't a string either. My guess
// is that the module isn't ready to work with ESM despite the build.
let Dynamsoft = 'DWT' in _Dynamsoft ? _Dynamsoft : window.Dynamsoft
let [major, minor, patch] = Dynamsoft.DWT.JSVersion.split(',')
let ResourcesPath = `${window.location.origin}/dwt-resources/${major}-${minor}-${patch}`

// https://www.dynamsoft.com/web-twain/docs/info/api/appendix.html
let USER_ERROR_CODES = [-1032]

export function useDwt(props) {
  let [state, setState] = useState({
    debug: [],
    sources: [],
    useNewDrivers: false,
    currentSource: null,
    DWObject: null,
    isLoading: false,
    isAcquiring: false,
  })

  function debug(message, context = {}) {
    setState(prev => ({
      ...prev,
      debug: [
        ...prev.debug,
        `${Date.now()} - ${message}: ${JSON.stringify(context, null, 2)}`,
      ],
    }))
  }

  useEffect(() => {
    debug('useEffect: Initializing DWT')
    setState(prev => ({ ...prev, isLoading: true }))
    debug('State: isLoading set to true')

    Dynamsoft.DWT.RegisterEvent('OnWebTwainReady', async () => {
      debug('Event: OnWebTwainReady fired')

      let dwObject = Dynamsoft.DWT.GetWebTwain(props.containerId)
      debug('GetWebTwain called', { containerId: props.containerId })
      // https://www.dynamsoft.com/web-twain/docs/faq/general-troubleshooting-steps.html#19min
      if (window.GreyfinchDebugTwain) {
        dwObject.LogLevel = 1
        debug(
          `LogLevel set to 1. Logs will be on Windows C:\\Windows\\SysWOW64\\Dynamsoft\\DynamsoftServicex64_{version number}\\log or C:\\Users\\{UserName}\\AppData\\Roaming\\Dynamsoft\\DynamsoftService\\log. macOS, Go > Applications > Dynamsoft > DynamsoftServicex64_{version number} > {installed version No.} > log`
        )
      } else {
        debug('LogLevel set to 0')
        dwObject.LogLevel = 0
      }

      if (dwObject) {
        let vCount = dwObject.SourceCount
        debug('SourceCount', { vCount })
        debug('Platform', { env: Dynamsoft.Lib.env })

        if (vCount === 0 && Dynamsoft.Lib.env.bMac) {
          debug('Mac platform with 0 sources detected, attempting to fix...')
          await dwObject.CloseSourceManagerAsync()
          debug('CloseSourceManager called')
          dwObject.ImageCaptureDriverType = 0
          debug('ImageCaptureDriverType set to 0')
          await dwObject.OpenSourceManagerAsync()
          debug('OpenSourceManager called')
          vCount = dwObject.SourceCount
          debug('New SourceCount', { vCount })
        }

        let defaultSource = getDefaultSource()
        debug('Default source from localStorage', { defaultSource })

        let sourceNames = []
        for (let i = 0; i < vCount; i++) {
          let sourceName = dwObject.GetSourceNameItems(i)
          debug(`Source ${i}`, { sourceName })
          if (sourceName === defaultSource) {
            sourceNames.unshift(sourceName)
            debug(`Source ${i} matched default, moved to front`)
          } else {
            sourceNames.push(sourceName)
          }
        }
        debug('Final source list', { sourceNames })

        setState(prev => ({
          ...prev,
          DWObject: dwObject,
          sources: sourceNames,
          currentSource: sourceNames.length
            ? sourceNames[0]
            : state.currentSource,
          isLoading: false,
        }))
        debug('State updated', {
          sources: sourceNames,
          currentSource: sourceNames[0] || state.currentSource,
          isLoading: false,
        })
      } else {
        debug('ERROR: dwObject is null')
      }
    })

    loadDWT()
    return () => {
      debug('useEffect cleanup: Unloading DWT')
      Dynamsoft.DWT.Unload()
    }
  }, []) // eslint-disable-line

  return {
    ...state,
    onSourceChange,
    onImagesAcquire,
    toggleUseNewDrivers: () =>
      setState(prev => ({ ...prev, useNewDrivers: !prev.useNewDrivers })),
  }

  function onSourceChange(value) {
    debug('onSourceChange called', { value })
    setDefaultSource(value)
    setState(prev => ({ ...prev, currentSource: value }))
    debug('Current source updated', { value })
  }

  async function onImagesAcquire() {
    debug('onImagesAcquire called')
    debug('Current source', { currentSource: state.currentSource })

    if (!state.currentSource) {
      debug('ERROR: No current source, returning early')
      return
    }

    debug('Calling RemoveAllImages')
    state.DWObject.RemoveAllImages()

    debug('Setting IfDisableSourceAfterAcquire to true')
    state.DWObject.IfDisableSourceAfterAcquire = true

    debug('Calling CloseSource')
    await state.DWObject.CloseSourceAsync()

    debug('Calling CloseSourceManager')
    await state.DWObject.CloseSourceManagerAsync()

    debug('Searching for source', {
      currentSource: state.currentSource,
      sourceCount: state.DWObject.SourceCount,
    })
    for (let i = 0; i < state.DWObject.SourceCount; i++) {
      let sourceName = state.DWObject.GetSourceNameItems(i)
      debug(`Checking source ${i}`, { sourceName })
      if (sourceName === state.currentSource) {
        debug(
          `Found matching source at index ${i}, calling SelectSourceByIndex`
        )
        await state.DWObject.SelectSourceByIndexAsync(i)
        break
      }
    }

    debug('Calling OpenSource')
    await state.DWObject.OpenSourceAsync()

    if (state.useNewDrivers) {
      debug('Setting IfUseTwainDSM to true')
      state.DWObject.IfUseTwainDSM = true
    }

    debug('Setting IfShowUI to true')
    state.DWObject.IfShowUI = true

    debug('Calling AcquireImage with callbacks')
    state.DWObject.AcquireImage(onAcquireImageSuccess, onAcquireImageFailure)

    function onAcquireImageSuccess() {
      debug('onAcquireImageSuccess called')
      debug('State: isAcquiring set to true')

      let imagesInBuffer = state.DWObject.HowManyImagesInBuffer
      debug('Images in buffer', { imagesInBuffer })

      if (imagesInBuffer < 1) {
        debug('ERROR: No images in buffer')
        console.error({
          type: 'twain/upload/no-images',
          ErrorString: state.DWObject.ErrorString,
        })
        debug('State: isAcquiring set to false')
        return
      }

      let indices = []
      for (let i = 0; i < imagesInBuffer; i++) {
        let bitDepth = state.DWObject.GetImageBitDepth(i)
        debug(`Image ${i} bit depth`, { bitDepth })

        if (bitDepth === 1) {
          debug(`Image ${i} is 1-bit, converting to grayscale`)
          state.DWObject.ConvertToGrayScale(i)
        }

        indices.push(i)
      }
      debug('Indices to process', { indices })

      let convertImagePromise
      if (props.convertMultiPageToPdf) {
        debug('Converting as multi-page PDF')
        convertImagePromise = new Promise((resolve, reject) => {
          let name = `${formatDate(new Date(), 'yyyyMMdd')}-${
            state.currentSource
          }-${uuid().substring(0, 7)}.pdf`
          let type = 'application/pdf'
          debug('Converting to PDF', { name, indices })

          state.DWObject.ConvertToBlob(
            indices,
            Dynamsoft.DWT.EnumDWT_ImageType.IT_PDF,
            function (result, indices, resultType) {
              debug('PDF converted successfully', {
                size: result.size,
                indices,
                resultType,
              })

              resolve([{ name, type, size: result.size, data: result }])
            },
            function (errorCode, errorString) {
              debug('ERROR: PDF conversion failed', { errorCode, errorString })
              console.error({
                type: 'twain/upload/pdf/not-converted',
                errorCode,
                errorString,
              })
              reject({ errorCode, errorString })
            }
          )
        })
      } else {
        debug('Converting as individual JPG images', {
          imagesInBuffer,
          convertMultiPageToPdf: props.convertMultiPageToPdf,
        })
        convertImagePromise = Promise.all(
          indices.map(
            i =>
              new Promise((resolve, reject) => {
                let name = `${formatDate(new Date(), 'yyyyMMdd')}-${
                  state.currentSource
                }-${uuid().substring(0, 7)}.jpg`
                let type = 'image/jpeg'
                debug(`Converting image ${i} to JPG`, { name })

                state.DWObject.ConvertToBlob(
                  [i],
                  Dynamsoft.DWT.EnumDWT_ImageType.IT_JPG,
                  function (result, indices, resultType) {
                    debug(`Image ${i} converted to JPG successfully`, {
                      size: result.size,
                      indices,
                      resultType,
                    })
                    resolve({ name, type, size: result.size, data: result })
                  },
                  function (errorCode, errorString) {
                    debug(`ERROR: Image ${i} conversion to JPG failed`, {
                      errorCode,
                      errorString,
                    })
                    console.error({
                      type: 'twain/upload/image/not-converted',
                      i,
                      errorCode,
                      errorString,
                    })
                    reject({ i, errorCode, errorString })
                  }
                )
              })
          )
        )
      }

      // leaving it to return a Promise because changing it to async/await results in an error being thrown from Dynamsoft
      return convertImagePromise
        .then(imageSource => {
          debug('Image conversion completed', { imageSource })
          debug('Calling CloseSource')
          state.DWObject.CloseSource()

          if (props.onImagesAcquired) {
            debug('Calling props.onImagesAcquired with imageSource')
            return props.onImagesAcquired(imageSource)
          } else {
            debug('No onImagesAcquired callback provided')
          }
        })
        .catch(error => {
          debug('ERROR in conversion promise chain', { error })
          captureError(error, { type: 'twain/upload/error' })
        })
    }

    function onAcquireImageFailure(errorCode, errorString) {
      debug('onAcquireImageFailure called', { errorCode, errorString })

      if (
        USER_ERROR_CODES.includes(errorCode) &&
        state.DWObject.HowManyImagesInBuffer > 0
      ) {
        debug(
          'Calling onAcquireImageSuccess because even though we got an user cancelled error, there are images in the buffer',
          { imagesInBuffer: state.DWObject.HowManyImagesInBuffer }
        )
        onAcquireImageSuccess()
        return
      }

      debug('Calling CloseSource')
      state.DWObject.CloseSource()

      // do not report errors caused by user's action such as cancelling the scanning
      if (USER_ERROR_CODES.includes(errorCode)) {
        debug('Error code is a user error (cancellation), not reporting', {
          errorCode,
        })
        return
      }

      debug('Capturing error with captureError')
      captureError(errorCode, {
        type: 'twain/acquireImage/failure',
        ErrorString: errorString,
      })
    }
  }

  function loadDWT() {
    debug('loadDWT called')
    debug('Setting ResourcesPath', { ResourcesPath })
    Dynamsoft.DWT.ResourcesPath = ResourcesPath

    debug('Setting ProductKey', {
      ProductKey: ProductKey ? '[REDACTED]' : 'undefined',
    })
    Dynamsoft.DWT.ProductKey = ProductKey

    let containers = [
      { ContainerId: props.containerId, Width: width, Height: height },
    ]
    debug('Setting Containers', { containers })
    Dynamsoft.DWT.Containers = containers

    debug('Setting IfAddMD5InUploadHeader to false')
    Dynamsoft.DWT.IfAddMD5InUploadHeader = false

    debug('Setting IfConfineMaskWithinTheViewer to false')
    Dynamsoft.DWT.IfConfineMaskWithinTheViewer = false

    debug('Calling modulizeInstallJS')
    modulizeInstallJS()

    debug('Calling Dynamsoft.DWT.Load()')
    Dynamsoft.DWT.Load()
  }

  function modulizeInstallJS() {
    debug('modulizeInstallJS: Wrapping Dynamsoft callbacks')

    let _DWT_Reconnect = Dynamsoft.DWT_Reconnect
    Dynamsoft.DWT_Reconnect = (...args) => {
      debug('Dynamsoft.DWT_Reconnect called', { args })
      return _DWT_Reconnect.call({ Dynamsoft: Dynamsoft }, ...args)
    }

    let __show_install_dialog = Dynamsoft._show_install_dialog
    Dynamsoft._show_install_dialog = (...args) => {
      debug('Dynamsoft._show_install_dialog called', { args })
      return __show_install_dialog.call({ Dynamsoft: Dynamsoft }, ...args)
    }

    Dynamsoft.DWT.OnWebTwainPreExecute = function () {
      debug('Dynamsoft.OnWebTwainPreExecute called')
      setState(prev => ({ ...prev, isAcquiring: true }))
    }
    Dynamsoft.DWT.OnWebTwainPostExecute = function () {
      debug('Dynamsoft.OnWebTwainPostExecute called')
      setState(prev => ({ ...prev, isAcquiring: false }))
    }

    let _OnWebTwainOldPluginNotAllowedCallback =
      Dynamsoft.OnWebTwainOldPluginNotAllowedCallback
    Dynamsoft.OnWebTwainOldPluginNotAllowedCallback = (...args) => {
      debug('Dynamsoft.OnWebTwainOldPluginNotAllowedCallback called', { args })
      return _OnWebTwainOldPluginNotAllowedCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnWebTwainNeedUpgradeCallback = Dynamsoft.OnWebTwainNeedUpgradeCallback
    Dynamsoft.OnWebTwainNeedUpgradeCallback = (...args) => {
      debug('Dynamsoft.OnWebTwainNeedUpgradeCallback called', { args })
      return _OnWebTwainNeedUpgradeCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnWebTwainPreExecuteCallback = Dynamsoft.OnWebTwainPreExecuteCallback
    Dynamsoft.OnWebTwainPreExecuteCallback = (...args) => {
      debug('Dynamsoft.OnWebTwainPreExecuteCallback called', { args })
      return _OnWebTwainPreExecuteCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnWebTwainPostExecuteCallback = Dynamsoft.OnWebTwainPostExecuteCallback
    Dynamsoft.OnWebTwainPostExecuteCallback = (...args) => {
      debug('Dynamsoft.OnWebTwainPostExecuteCallback called', { args })
      return _OnWebTwainPostExecuteCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnRemoteWebTwainNotFoundCallback =
      Dynamsoft.OnRemoteWebTwainNotFoundCallback
    Dynamsoft.OnRemoteWebTwainNotFoundCallback = (...args) => {
      debug('Dynamsoft.OnRemoteWebTwainNotFoundCallback called', { args })
      return _OnRemoteWebTwainNotFoundCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnRemoteWebTwainNeedUpgradeCallback =
      Dynamsoft.OnRemoteWebTwainNeedUpgradeCallback
    Dynamsoft.OnRemoteWebTwainNeedUpgradeCallback = (...args) => {
      debug('Dynamsoft.OnRemoteWebTwainNeedUpgradeCallback called', { args })
      return _OnRemoteWebTwainNeedUpgradeCallback.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    let _OnWebTWAINDllDownloadFailure = Dynamsoft.OnWebTWAINDllDownloadFailure
    Dynamsoft.OnWebTWAINDllDownloadFailure = (...args) => {
      debug('Dynamsoft.OnWebTWAINDllDownloadFailure called', { args })
      return _OnWebTWAINDllDownloadFailure.call(
        { Dynamsoft: Dynamsoft },
        ...args
      )
    }

    debug('modulizeInstallJS: All callbacks wrapped')
  }

  function setDefaultSource(sourceName) {
    debug('setDefaultSource called', { sourceName })
    try {
      localStorage.setItem('dwt:defaultSource', sourceName)
      debug('Default source saved to localStorage')
    } catch (error) {
      debug('ERROR: Failed to save default source to localStorage', { error })
      console.error('Failed to save default DWT source to localStorage', error)
    }
  }

  function getDefaultSource() {
    debug('getDefaultSource called')
    try {
      let source = localStorage.getItem('dwt:defaultSource')
      debug('Retrieved default source from localStorage', { source })
      return source
    } catch (error) {
      debug('ERROR: Failed to get default source from localStorage', { error })
      console.error('Failed to get default DWT source from localStorage', error)
    }
  }
}
